/**
 * ч.1398 (рішення власника 2026-10-29): «Баланс і тарифи» — баланс акаунта, підписки баз,
 * сховище, кредити Oblik+/AI, журнал руху. Усе списується з балансу; дії — лише власник акаунта.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ACT_STATUS_UK, billing, CARD_STATUS_UK, fmtBytes, fmtDate, fmtUah, INVOICE_STATUS_UK, LEDGER_KIND_UK, STATUS_UK,
  type BaseSubscription, type BillingAct, type BillingInvoice, type CardPayment, type LedgerRow,
} from '@/api/billing';
import { getMe } from '@/api/auth';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import BillingActSignDialog from './BillingActSignDialog';

const STATUS_BADGE: Record<string, string> = {
  TRIAL: 'var(--badge-blue-bg)', ACTIVE: 'var(--badge-green-bg)', GRACE: 'var(--badge-amber-bg)',
  BLOCKED: 'var(--badge-red-bg)', TESTER: 'var(--badge-blue-bg)',
};
const STATUS_FG: Record<string, string> = {
  TRIAL: 'var(--badge-blue-fg)', ACTIVE: 'var(--badge-green-fg)', GRACE: 'var(--badge-amber-fg)',
  BLOCKED: 'var(--badge-red-fg)', TESTER: 'var(--badge-blue-fg)',
};

function errText(e: unknown): string {
  const d = (e as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  return typeof d === 'string' ? d : 'Не вдалося виконати дію';
}

export function StatusBadge({ status }: { status: string }) {
  return (
    <span style={{ background: STATUS_BADGE[status] ?? 'var(--bg-soft)', color: STATUS_FG[status] ?? 'var(--text)',
      borderRadius: 4, padding: '0 6px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {STATUS_UK[status as keyof typeof STATUS_UK] ?? status}
    </span>
  );
}

export default function BillingSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);
  const [packs, setPacks] = useState(1);
  const [invAmount, setInvAmount] = useState('150');
  const [invOrg, setInvOrg] = useState('');
  const [invPayer, setInvPayer] = useState('');
  const [invCode, setInvCode] = useState('');
  const [invEmail, setInvEmail] = useState('');
  const [invResult, setInvResult] = useState<string | null>(null);
  const [cardAmount, setCardAmount] = useState('150');
  const [cardResult, setCardResult] = useState<string | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [signAct, setSignAct] = useState<BillingAct | null>(null);
  const [params, setParams] = useSearchParams();
  const cardsQ = useQuery({ queryKey: ['billing-card-payments'], queryFn: billing.cardPayments });
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe, staleTime: 60_000 });
  const invQ = useQuery({ queryKey: ['billing-invoices'], queryFn: billing.invoices });
  const q = useQuery({ queryKey: ['billing-summary'], queryFn: billing.summary });
  const ledgerQ = useQuery({ queryKey: ['billing-ledger'], queryFn: () => billing.ledger(200) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['billing-summary'] });
    qc.invalidateQueries({ queryKey: ['billing-ledger'] });
    qc.invalidateQueries({ queryKey: ['billing-notices'] });
    qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    qc.invalidateQueries({ queryKey: ['billing-card-payments'] });
    qc.invalidateQueries({ queryKey: ['ai-balance'] });
    qc.invalidateQueries({ queryKey: ['ai-credit-history'] });
  };
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e) => setError(errText(e)),
  });
  const d = q.data;
  const t = d?.tariffs;
  const isOwner = !!d?.is_owner;
  const payerOrgs = d?.organizations ?? [];
  const requestInvoice = useMutation({
    mutationFn: () => billing.requestInvoice({
      amount: Number(invAmount),
      organization_id: invOrg && invOrg !== '__other' ? invOrg : null,
      payer_name: invOrg === '__other' ? invPayer : null,
      payer_code: invOrg === '__other' ? (invCode || null) : null,
      email: invEmail || null,
    }),
    onSuccess: (inv) => {
      setError(null);
      setInvResult(`Рахунок № ${inv.number} на ${fmtUah(inv.amount)} виписано${inv.email_sent ? ` і надіслано на ${inv.email}` : ' — завантажте PDF у таблиці нижче'}.`);
      refresh();
    },
    onError: (e) => setError(errText(e)),
  });
  const payByCard = useMutation({
    mutationFn: () => billing.createCardPayment(Number(cardAmount)),
    onSuccess: (p) => {
      setError(null);
      if (p.page_url) window.location.assign(p.page_url);
      else setCardResult('Платіж створено, але сторінку оплати не отримано — спробуйте ще раз.');
    },
    onError: (e) => setError(errText(e)),
  });
  // ч.1400: повернення зі сторінки оплати (?card_payment=id) — показати результат, оновити баланс.
  const returned = params.get('card_payment');
  useEffect(() => {
    if (!returned) return;
    let tries = 0;
    const poll = async () => {
      try {
        const p = await billing.cardPayment(returned);
        if (p.status === 'success') {
          setCardResult(`Оплату карткою на ${fmtUah(p.paid_amount ?? p.amount)} зараховано.`);
          refresh();
        } else if (p.status === 'failure' || p.status === 'expired' || p.status === 'reversed') {
          setCardResult(`Оплата карткою: ${CARD_STATUS_UK[p.status]}${p.failure_reason ? ` (${p.failure_reason})` : ''}.`);
        } else if (tries++ < 6) {
          setCardResult('Чекаємо підтвердження оплати від банку…');
          setTimeout(poll, 3000);
          return;
        } else {
          setCardResult('Банк ще не підтвердив оплату — баланс оновиться автоматично, коли підтвердження надійде.');
        }
      } catch (e) {
        setError(errText(e));
      }
      params.delete('card_payment');
      setParams(params, { replace: true });
    };
    void poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returned]);
  const cardCols: ColumnDef<CardPayment>[] = [
    { key: 'created_at', header: 'Дата', width: 150, sortValue: (r) => r.created_at, cell: (r) => new Date(r.created_at).toLocaleString('uk-UA') },
    { key: 'amount', header: 'Сума', width: 120, align: 'right', sortValue: (r) => Number(r.amount), cell: (r) => fmtUah(r.paid_amount ?? r.amount) },
    { key: 'status', header: 'Стан', width: 180, cell: (r) => `${CARD_STATUS_UK[r.status] ?? r.status}${r.failure_reason ? ` · ${r.failure_reason}` : ''}` },
    { key: 'act', header: '', width: 140, cell: (r) => (r.status === 'created' && r.page_url
      ? <a href={r.page_url}>перейти до оплати</a> : null) },
  ];
  const invoiceCols: ColumnDef<BillingInvoice>[] = [
    { key: 'invoice_date', header: 'Дата', width: 110, sortValue: (r) => r.invoice_date, cell: (r) => fmtDate(r.invoice_date) },
    { key: 'number', header: 'Номер', width: 130, sortValue: (r) => r.number, cell: (r) => r.number },
    { key: 'payer_name', header: 'Платник', cell: (r) => `${r.payer_name}${r.payer_code ? ` (${r.payer_code})` : ''}`, sortValue: (r) => r.payer_name },
    { key: 'amount', header: 'Сума', width: 120, align: 'right', sortValue: (r) => Number(r.amount), cell: (r) => fmtUah(r.amount) },
    { key: 'status', header: 'Стан', width: 140, sortValue: (r) => r.status,
      cell: (r) => <span>{INVOICE_STATUS_UK[r.status]}{r.status === 'PAID' && r.paid_at ? ` ${fmtDate(r.paid_at)}` : ''}{r.status === 'ISSUED' && r.valid_until ? ` до ${fmtDate(r.valid_until)}` : ''}</span> },
    { key: 'act', header: '', width: 290, cell: (r) => (
      <span style={{ display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        <button onClick={() => { billing.downloadInvoicePdf(r.id, `rahunok_${r.number}.pdf`).catch((e) => setError(errText(e))); }}>pdf</button>
        {isOwner && r.status === 'ISSUED' && (
          <button disabled={run.isPending || checking === r.id} title="Оплатили цей рахунок? Перевіримо виписку банку зараз"
            onClick={() => void checkPayment(r)}>перевірити оплату</button>
        )}
        {isOwner && r.status === 'ISSUED' && (
          <button disabled={run.isPending} onClick={async () => {
            if (await confirm({ title: 'Скасувати рахунок', message: `Скасувати рахунок № ${r.number}?`, okLabel: 'скасувати', danger: true })) run.mutate(() => billing.cancelInvoice(r.id));
          }}>скасувати</button>
        )}
      </span>
    ) },
  ];

  const checkPayment = async (r: BillingInvoice) => {
    setChecking(r.id);
    setError(null);
    try {
      const res = await billing.checkPayment(r.id);
      setInvResult(res.status === 'PAID' ? `Оплату за рахунком № ${r.number} зараховано.`
        : res.queued ? 'Перевіряємо виписку банку. Якщо оплата вже на рахунку, баланс оновиться за кілька хвилин.'
          : 'Виписку щойно перевірено: оплати ще немає. Банк може проводити платіж до наступного дня.');
      refresh();
      if (res.queued) window.setTimeout(refresh, 45_000);
    } catch (e) {
      setError(errText(e));
    } finally {
      setChecking(null);
    }
  };

  const formAct = useMutation({
    mutationFn: (ledgerId: string) => billing.formAct(ledgerId),
    onSuccess: (a) => { setError(null); setSignAct(a); refresh(); },
    onError: (e) => setError(errText(e)),
  });

  const act = async (label: string, fn: () => Promise<unknown>) => {
    if (await confirm({ title: 'Списання з балансу', message: label, okLabel: 'підтвердити' })) run.mutate(fn);
  };

  const cols: ColumnDef<BaseSubscription>[] = [
    { key: 'name', header: 'Організація', cell: (r) => r.name, sortValue: (r) => r.name },
    { key: 'status', header: 'Стан', width: 130, sortValue: (r) => r.status,
      cell: (r) => <span><StatusBadge status={r.status} />{r.plan === 'EDO' && <span style={{ marginLeft: 4, fontSize: '0.75rem', color: 'var(--muted)' }}>лише ЕДО</span>}</span> },
    {
      key: 'paid_until', header: 'Оплачено до', width: 150, sortValue: (r) => r.paid_until ?? '',
      cell: (r) => r.status === 'TESTER' ? '—' : (
        <span title={r.status === 'GRACE' ? `лише перегляд до ${fmtDate(r.grace_until)}; видалення ${fmtDate(r.delete_after)}`
          : r.status === 'BLOCKED' ? `без оплати базу буде видалено ${fmtDate(r.delete_after)}` : undefined}>
          {fmtDate(r.paid_until)}{r.days_left != null && r.days_left >= 0 && r.status !== 'BLOCKED' && r.status !== 'GRACE'
            ? <span style={{ color: 'var(--muted)' }}> · {r.days_left} дн.</span> : null}
        </span>
      ),
    },
    {
      key: 'storage', header: 'Сховище', width: 220, sortValue: (r) => r.used_bytes,
      cell: (r) => (
        <span>
          {fmtBytes(r.used_bytes)}{r.quota_bytes == null ? ' · обсяг не обмежується' : ` з ${fmtBytes(r.quota_bytes)}`}
          {r.paid_blocks > 0 && <span style={{ color: 'var(--muted)' }}> (+{r.paid_blocks} × {r.block_mb} МБ)</span>}
          {r.pending_blocks > 0 && (
            <span style={{ color: 'var(--badge-amber-fg)' }} title="Замовлені блоки увійдуть у наступне списання тарифу; місце додасться після оплати">
              {' '}· +{r.pending_blocks} × {r.block_mb} МБ після оплати
            </span>
          )}
        </span>
      ),
    },
    { key: 'monthly_price', header: 'Тариф / 30 дн.', width: 130, align: 'right', sortValue: (r) => Number(r.monthly_price),
      cell: (r) => r.status === 'TESTER' ? 'безкоштовно' : fmtUah(r.monthly_price) },
    ...(isOwner ? [{
      key: 'actions', header: '', width: 330,
      cell: (r: BaseSubscription) => r.status === 'TESTER' ? null : (
        <span style={{ display: 'inline-flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {r.status === 'BLOCKED'
            ? <button className="primary" disabled={run.isPending}
                onClick={() => act(`Відновити доступ до бази «${r.name}»: тариф ${fmtUah(r.monthly_price)} + премія за відновлення ${fmtUah(r.restore_fee)} = ${fmtUah(Number(r.monthly_price) + Number(r.restore_fee))}.`,
                  () => billing.restore(r.organization_id))}>відновити доступ</button>
            : <button disabled={run.isPending}
                title="Оплатити наступні 30 днів (достроково — від кінця оплаченого періоду)"
                onClick={() => act(`Продовжити базу «${r.name}» на 30 днів за ${fmtUah(r.monthly_price)}.`,
                  () => billing.renew(r.organization_id))}>продовжити на 30 дн.</button>}
          <button disabled={run.isPending || r.status === 'BLOCKED' || r.status === 'TRIAL'}
            title={r.status === 'TRIAL'
              ? 'У пробний період додаткове сховище недоступне — після оплати тарифу бази'
              : `Замовити ще ${r.block_mb} МБ: тариф зросте на ${fmtUah(r.block_price)} за 30 дн.; місце додасться після оплати тарифу («продовжити на 30 дн.» — оплатити зараз)`}
            onClick={() => act(`Замовити ще ${r.block_mb} МБ сховища базі «${r.name}»: тариф стане ${fmtUah(Number(r.monthly_price) + Number(r.block_price))} за 30 дн. Місце додасться після оплати тарифу — наступним списанням або одразу кнопкою «продовжити на 30 дн.».`,
              () => billing.setStorage(r.organization_id, r.extra_blocks + 1))}>+{r.block_mb} МБ</button>
          {r.extra_blocks > 0 && (
            <button disabled={run.isPending}
              title="Зменшити квоту (без повернення коштів за поточний період)"
              onClick={() => act(`Зменшити сховище бази «${r.name}» на ${r.block_mb} МБ (кошти за поточний період не повертаються).`,
                () => billing.setStorage(r.organization_id, r.extra_blocks - 1))}>−{r.block_mb} МБ</button>
          )}
          {r.plan === 'EDO' && (
            <button disabled={run.isPending} title="Повний тариф: облік, звіти, усі можливості сервісу (назад у «лише ЕДО» база не переводиться)"
              onClick={() => act(`Перевести базу «${r.name}» на повний тариф (${fmtUah(t?.base_price ?? 150)} за 30 дн.; якщо тариф ще не оплачувався — перший місяць безкоштовно).`,
                () => billing.setPlan(r.organization_id, 'BASE'))}>повний тариф</button>
          )}
        </span>
      ),
    } as ColumnDef<BaseSubscription>] : []),
  ];

  const ledgerCols: ColumnDef<LedgerRow>[] = [
    { key: 'created_at', header: 'Дата', width: 150, sortValue: (r) => r.created_at,
      cell: (r) => new Date(r.created_at).toLocaleString('uk-UA') },
    { key: 'kind', header: 'Операція', width: 190, sortValue: (r) => r.kind, cell: (r) => LEDGER_KIND_UK[r.kind] ?? r.kind },
    { key: 'amount', header: 'Сума', width: 130, align: 'right', sortValue: (r) => Number(r.amount),
      cell: (r) => <span style={{ color: Number(r.amount) < 0 ? 'var(--badge-red-fg)' : 'var(--badge-green-fg)', fontWeight: 600 }}>{fmtUah(r.amount)}</span> },
    { key: 'balance_after', header: 'Залишок', width: 130, align: 'right', cell: (r) => fmtUah(r.balance_after) },
    { key: 'act_doc', header: 'Акт', width: 240, cell: (r) => {
      if (!r.can_form_act) return null;
      if (!r.act_id) {
        return isOwner
          ? <button disabled={formAct.isPending} title={`Акт на суму і дату надходження${r.receipt_date ? ` ${fmtDate(r.receipt_date)}` : ''}`}
              onClick={() => formAct.mutate(r.id)}>сформувати акт</button>
          : <span style={{ color: 'var(--muted)' }}>формує власник акаунта</span>;
      }
      const name = `akt_${r.act_number}.pdf`.replace(/[/ ]/g, '-');
      return (
        <span style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span title={`Акт № ${r.act_number}`} style={{ fontSize: '0.8rem', color: r.act_status === 'DONE' ? 'var(--badge-green-fg)' : 'var(--muted)' }}>
            {r.act_status ? ACT_STATUS_UK[r.act_status] : ''}
          </span>
          {isOwner && r.act_status === 'FORMED' && (
            <button className="primary" onClick={() => setSignAct({
              id: r.act_id!, number: r.act_number ?? '', act_date: r.receipt_date ?? '', amount: r.amount,
              status: 'FORMED', payer_name: r.act_payer_name ?? '', payer_code: r.act_payer_code,
            })}>підписати і надіслати</button>
          )}
          <button onClick={() => { billing.downloadActPdf(r.act_id!, name).catch((e) => setError(errText(e))); }}>pdf</button>
          {r.act_status === 'DONE' && <Link to="/edo/journal">у журналі ЕДО</Link>}
        </span>
      );
    } },
    { key: 'organization', header: 'Організація', cell: (r) => r.organization ?? '', sortValue: (r) => r.organization ?? '' },
    { key: 'period', header: 'Період', width: 190, cell: (r) => r.period_from ? `${fmtDate(r.period_from)} – ${fmtDate(r.period_to)}` : '' },
    { key: 'note', header: 'Примітка', cell: (r) => r.note ?? '' },
  ];

  return (
    <div className="card">
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Баланс і тарифи</h2>
          <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
            Усі бази акаунта оплачуються з одного балансу у гривнях. Перший місяць кожної бази — безкоштовно.
          </div>
        </div>
      </div>

      {error && <div className="mc__panel mc__panel--warn" role="alert" style={{ marginBottom: '0.6rem' }}>{error}</div>}

      {d && (
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
          <div style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '0.7rem 1rem', minWidth: 200 }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Баланс</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }} data-testid="billing-balance">{fmtUah(d.balance)}</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
              {d.tester ? 'тестовий акаунт — бази безкоштовно' : `бази разом: ${fmtUah(d.monthly_total)} за 30 дн.`}
            </div>
          </div>
          <div style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '0.7rem 1rem', minWidth: 200 }}>
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Кредити Oblik+/AI</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{Number(d.credits_balance).toLocaleString('uk-UA', { maximumFractionDigits: 2 })}</div>
            {isOwner && t && (
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.3rem' }}>
                <input type="number" min={1} max={100} value={packs} style={{ width: 60 }} aria-label="Пакетів кредитів"
                  onChange={(e) => setPacks(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} />
                <button disabled={run.isPending}
                  onClick={() => act(`Купити ${packs * Number(t.credits_pack)} кредитів Oblik+/AI за ${fmtUah(packs * Number(t.credits_pack_price))} з балансу.`,
                    () => billing.buyCredits(packs))}>
                  купити {packs * Number(t.credits_pack)} за {fmtUah(packs * Number(t.credits_pack_price))}
                </button>
              </div>
            )}
          </div>
          <div style={{ background: 'var(--bg-soft)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '0.7rem 1rem', flex: '1 1 320px', fontSize: '0.85rem' }}>
            <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>Поповнення балансу</div>
            {d.invoices_enabled && isOwner ? (
              <div>
                <div style={{ marginBottom: '0.4rem' }}>
                  Безготівково за рахунком від ТОВ «Ейзі Україна»: рахунок надійде на пошту (PDF), після оплати
                  баланс поповниться автоматично за випискою банку. Призначення платежу скопіюйте з рахунка: за номером
                  оплату зараховано буде найшвидше. Ціни без ПДВ.
                </div>
                <div className="doc-grid">
                  <div className="field" style={{ gridColumn: 'span 5' }}>
                    <label>Платник</label>
                    <select value={invOrg} onChange={(e) => setInvOrg(e.target.value)} aria-label="Платник">
                      <option value="">— оберіть організацію —</option>
                      {payerOrgs.map((o) => <option key={o.organization_id} value={o.organization_id}>{o.name}</option>)}
                      <option value="__other">інший платник (назва і код)</option>
                    </select>
                  </div>
                  <div className="field" style={{ gridColumn: 'span 3' }}>
                    <label>Сума, грн</label>
                    <input type="number" min={50} max={200000} step="1" value={invAmount} onChange={(e) => setInvAmount(e.target.value)} aria-label="Сума рахунка" />
                  </div>
                  <div className="field" style={{ gridColumn: 'span 4' }}>
                    <label>Пошта для рахунка</label>
                    <input type="email" value={invEmail} placeholder={meQ.data?.email ?? ''} onChange={(e) => setInvEmail(e.target.value)} aria-label="Пошта для рахунка" />
                  </div>
                </div>
                {invOrg === '__other' && (
                  <div className="doc-grid">
                    <div className="field" style={{ gridColumn: 'span 8' }}>
                      <label>Назва платника</label>
                      <input value={invPayer} onChange={(e) => setInvPayer(e.target.value)} aria-label="Назва платника" />
                    </div>
                    <div className="field" style={{ gridColumn: 'span 4' }}>
                      <label>ЄДРПОУ / РНОКПП</label>
                      <input value={invCode} onChange={(e) => setInvCode(e.target.value)} aria-label="Код платника" />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.3rem' }}>
                  <button className="primary" disabled={requestInvoice.isPending || Number(invAmount) < 50 || !invOrg || (invOrg === '__other' && invPayer.trim().length < 3)}
                    onClick={() => requestInvoice.mutate()}>запросити рахунок на {fmtUah(Number(invAmount) || 0)}</button>
                  {invResult && <span style={{ color: 'var(--muted)' }}>{invResult}</span>}
                </div>
              </div>
            ) : (
              <div>
                Безготівково — за рахунком від ТОВ «Ейзі Україна»{isOwner ? '' : ' (рахунок запитує власник акаунта)'}.
                {!d.invoices_enabled && ' Виставлення рахунків ще не налаштовано — напишіть у підтримку (кнопка «?» праворуч унизу).'}
                {!d.card_payments_enabled && ' Оплата карткою — незабаром.'} Ціни без ПДВ.
              </div>
            )}
            {isOwner && (
              <div style={{ marginTop: '0.6rem', borderTop: '1px solid var(--border-soft)', paddingTop: '0.5rem' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.2rem' }}>Карткою</div>
                {d.card_payments_enabled ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input type="number" min={50} max={200000} step="1" value={cardAmount} style={{ width: 110 }}
                      onChange={(e) => setCardAmount(e.target.value)} aria-label="Сума оплати карткою" />
                    <button className="primary" disabled={payByCard.isPending || Number(cardAmount) < 50}
                      onClick={() => payByCard.mutate()}>оплатити карткою {fmtUah(Number(cardAmount) || 0)}</button>
                    <span style={{ color: 'var(--muted)' }}>платіжна сторінка monobank; баланс поповниться одразу після підтвердження</span>
                  </div>
                ) : <span style={{ color: 'var(--muted)' }}>оплата карткою ще не підключена</span>}
                {cardResult && <div style={{ marginTop: '0.3rem' }}>{cardResult}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {t && (
        <details style={{ marginBottom: '0.9rem', fontSize: '0.85rem' }}>
          <summary style={{ cursor: 'pointer' }}>Тарифи</summary>
          <ul style={{ margin: '0.4rem 0 0 1rem', padding: 0 }}>
            <li>База (організація): <b>{fmtUah(t.base_price)}</b> за {t.period_days} днів, у тарифі {t.base_quota_mb} МБ сховища документів.
              Перші {t.trial_days} днів кожної бази — безкоштовно.</li>
            <li>Додаткове сховище: кожні {t.extra_block_mb} МБ — <b>{fmtUah(t.extra_block_price)}</b> за {t.period_days} днів на базу (обираєте, якій базі).
              Замовлення піднімає тариф бази; місце додається після оплати тарифу. У пробний період — недоступне.</li>
            <li>Кредити Oblik+/AI: <b>{Number(t.credits_pack).toFixed(0)}</b> кредитів за {fmtUah(t.credits_pack_price)}; спільні для всіх баз акаунта.
              Після оплати тарифу — подарунок {Number(t.gift_credits).toFixed(0)} кредитів, раз на календарний місяць.</li>
            <li>Коли оплачений період закінчується, тариф списується з балансу автоматично. Якщо коштів не вистачає —
              {' '}{t.grace_days} днів база доступна лише для перегляду, далі стає недоступною; відновлення — тариф + премія
              у розмірі тарифу. Через {t.purge_days} днів без оплати базу буде видалено.</li>
            <li>Режим «ЕДО» (зберігання і підписання електронних документів без обліку): безкоштовно {t.edo_free_docs} документів
              на всі картки акаунта разом (обсяг файлів не обмежується), далі сховище по {t.edo_block_mb} МБ за <b>{fmtUah(t.edo_block_price)}</b>
              на {t.period_days} днів на картку (оплачена картка без ліміту за кількістю документів).</li>
            <li>Тестові акаунти (позначку ставить власник платформи) — безкоштовно.</li>
          </ul>
        </details>
      )}

      <h3 style={{ fontSize: '0.95rem', margin: '0.6rem 0 0.3rem' }}>Бази</h3>
      <DataTable<BaseSubscription> rows={d?.organizations ?? []} columns={cols} rowKey={(r) => r.organization_id}
        loading={q.isLoading} emptyText="Організацій ще немає." initialSortKey="name" />

      {(cardsQ.data?.items.length ?? 0) > 0 && (
        <>
          <h3 style={{ fontSize: '0.95rem', margin: '1rem 0 0.3rem' }}>Оплати карткою</h3>
          <DataTable<CardPayment> rows={cardsQ.data?.items ?? []} columns={cardCols} rowKey={(r) => r.id}
            loading={cardsQ.isLoading} emptyText="Оплат карткою ще не було." initialSortKey="created_at" initialSortDir="desc" />
        </>
      )}

      {(invQ.data?.items.length ?? 0) > 0 && (
        <>
          <h3 style={{ fontSize: '0.95rem', margin: '1rem 0 0.3rem' }}>Рахунки на поповнення</h3>
          <DataTable<BillingInvoice> rows={invQ.data?.items ?? []} columns={invoiceCols} rowKey={(r) => r.id}
            loading={invQ.isLoading} emptyText="Рахунків ще не було." initialSortKey="invoice_date" initialSortDir="desc" />
        </>
      )}

      <h3 style={{ fontSize: '0.95rem', margin: '1rem 0 0.3rem' }}>Рух коштів</h3>
      <DataTable<LedgerRow> rows={ledgerQ.data?.items ?? []} columns={ledgerCols} rowKey={(r) => r.id}
        loading={ledgerQ.isLoading} emptyText="Руху коштів ще не було." initialSortKey="created_at" initialSortDir="desc" />
      {signAct && (
        <BillingActSignDialog act={signAct} onClose={() => setSignAct(null)}
          onSigned={() => { setSignAct(null); refresh(); }} />
      )}
    </div>
  );
}
